import React, {useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputText} from "primereact/inputtext";
import {Tag} from "primereact/tag";
import {STATUS_LABELS, STATUS_SEVERITY, TYPE_LABELS, articleOfVideo, isVideo, tagsOf, videoOfArticle} from "./help-cms";
import {TabHeader} from "./ui";

const CHIPS = [
    ["all", "همه"],
    ["article", "مقاله"],
    ["video", "ویدئو"],
    ["review", "نیازمند بازبینی"],
];

function Dot({on, label}) {
    return <span className={`text-xs ml-2 ${on ? "text-green-600" : "text-400"}`}>● {label}</span>;
}

export default function ContentsTab({data, filter, setFilter, openTab, onNew, onEdit}) {
    const {contents, categories, loading} = data;
    const [query, setQuery] = useState("");
    const chip = filter.status === "review" ? "review" : filter.type || "all";
    const categoryTitle = (slug) => categories.find((c) => c.slug === slug)?.title || "-";

    const rows = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        return contents.filter((item) => {
            if (chip === "article" && isVideo(item)) return false;
            if (chip === "video" && !isVideo(item)) return false;
            if (chip === "review" && item.contentStatus !== "review") return false;
            if (filter.tag && !tagsOf(item).includes(filter.tag)) return false;
            if (filter.category && item.categorySlug !== filter.category) return false;
            return !normalized || [item.title, item.summary, ...tagsOf(item)].some((value) => String(value || "").toLowerCase().includes(normalized));
        });
    }, [contents, chip, query, filter]);

    const pick = (key) => setFilter(key === "review" ? {status: "review"} : key === "all" ? {} : {type: key});

    return (
        <div className="flex flex-column gap-3">
            <TabHeader kicker="محتواها" title="یک فهرست برای همه محتواها" description="مقاله و ویدئو جدا هستند، اما از همین‌جا مدیریت می‌شوند." onNew={onNew}/>

            <div className="flex flex-wrap align-items-center gap-2">
                <span className="p-input-icon-right flex-1 min-w-16rem">
                    <i className="pi pi-search"/>
                    <InputText className="w-full" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="جستجوی عنوان، توضیح یا برچسب"/>
                </span>
                {CHIPS.map(([key, label]) => (
                    <Button key={key} className="text-xs p-button-sm" outlined={chip !== key} label={label} onClick={() => pick(key)}/>
                ))}
                {filter.tag && <Button className="text-xs p-button-sm" icon="pi pi-times" iconPos="right" label={`#${filter.tag}`} onClick={() => setFilter({...filter, tag: undefined})}/>}
            </div>

            <div className="surface-card border-1 border-200 border-round-xl overflow-hidden">
                <DataTable value={rows} dataKey="documentId" loading={loading} rowHover emptyMessage="رکوردی یافت نشد" tableClassName="text-sm"
                           onRowClick={(e) => onEdit(e.data)} rowClassName={() => "cursor-pointer"}>
                    <Column header="عنوان" bodyClassName="text-right" headerClassName="text-right"
                            body={(item) => (
                                <div className="flex flex-column gap-1">
                                    <b>{item.title}</b>
                                    <span className="text-xs text-400">{tagsOf(item).slice(0, 2).map((tag) => `#${tag}`).join("  ")}</span>
                                </div>
                            )}/>
                    <Column header="نوع و موضوع" bodyClassName="text-right" headerClassName="text-right"
                            body={(item) => (
                                <div className="flex flex-column gap-1 align-items-start">
                                    <Tag value={TYPE_LABELS[item.contentType]} severity={isVideo(item) ? "info" : null}/>
                                    <small className="text-500">{categoryTitle(item.categorySlug)}</small>
                                </div>
                            )}/>
                    <Column header="ارتباط مکمل" bodyClassName="text-right" headerClassName="text-right"
                            body={(item) => {
                                const pair = isVideo(item) ? articleOfVideo(contents, item) : videoOfArticle(contents, item);
                                return pair ? (
                                    <Button className="p-button-outlined p-button-sm text-xs" label={isVideo(item) ? "▤ مقاله مرتبط" : "▶ ویدئوی مرتبط"}
                                            onClick={(e) => { e.stopPropagation(); onEdit(pair); }}/>
                                ) : (
                                    <Button className="p-button-outlined p-button-warning p-button-sm text-xs" label="+ اتصال محتوا"
                                            onClick={(e) => { e.stopPropagation(); openTab("relations"); }}/>
                                );
                            }}/>
                    <Column header="نمایش" bodyClassName="text-right" headerClassName="text-right"
                            body={(item) => (
                                <span className="white-space-nowrap">
                                    <Dot on={item.surfaces?.public !== false} label="عمومی"/>
                                    <Dot on={Boolean(item.surfaces?.widget)} label="ویجت"/>
                                </span>
                            )}/>
                    <Column header="وضعیت" bodyClassName="text-right" headerClassName="text-right"
                            body={(item) => <Tag value={STATUS_LABELS[item.contentStatus]} severity={STATUS_SEVERITY[item.contentStatus]}/>}/>
                    <Column header="" bodyClassName="text-right"
                            body={(item) => <Button className="p-button-outlined p-button-sm text-xs" label="ویرایش" onClick={(e) => { e.stopPropagation(); onEdit(item); }}/>}/>
                </DataTable>
            </div>
        </div>
    );
}
